import { useState } from "react";
import { EyeOff } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { signInWithPassword } from "@/api/auth";
import { logger } from "@/lib/logger";
import { getErrorMessage } from "@/lib/getErrorMessage";
import { cn } from "@/lib/utils";
import { AuthCard } from "./AuthCard";
import { ValidatedField } from "./ValidatedField";
import { useFieldValidation } from "./useFieldValidation";
import { ViewIcon, MailIcon } from "@/components/icons/actions";

import { LockIcon } from "@/components/icons/status";

const emailSchema = z.string().email("Email invalide");

interface LoginFormProps {
  onSwitchMode: (mode: "signup" | "forgot-password") => void;
}

export function LoginForm({ onSwitchMode }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const emailValidation = useFieldValidation({ value: email, schema: emailSchema, required: true });
  const passwordValidation = useFieldValidation({
    value: password,
    required: true,
    requiredMessage: "Le mot de passe est requis",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    emailValidation.markTouched();
    passwordValidation.markTouched();

    if (emailValidation.state === "invalid" || passwordValidation.state === "invalid") return;
    if (!email.trim() || !password) return;

    setIsLoading(true);
    try {
      const { error } = await signInWithPassword(email, password);
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Erreur de connexion", { description: "Email ou mot de passe incorrect." });
        } else {
          throw error;
        }
        return;
      }
      toast.success("Connexion réussie", { description: "Bienvenue sur Equiply !" });
    } catch (err: unknown) {
      logger.error("Auth login error:", err);
      toast.error("Erreur", { description: getErrorMessage(err, "Une erreur est survenue. Veuillez réessayer.") });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="Bon retour"
      subtitle="Connectez-vous à votre espace gérant ou salarié."
      bottomSlot={
        <p className="text-sm text-muted-foreground">
          Pas d'espace gérant ?{" "}
          <Button
            type="button"
            variant="link"
            onClick={() => onSwitchMode("signup")}
            className="px-1 h-auto text-sm font-semibold"
          >
            Créer un espace
          </Button>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <ValidatedField
          label="Email"
          type="email"
          inputMode="email"
          leadingIcon={<MailIcon />}
          placeholder="jean.dupont@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={emailValidation.onBlur}
          disabled={isLoading}
          autoComplete="email"
          validation={emailValidation}
        />

        <ValidatedField
          label="Mot de passe"
          labelAction={
            <Button
              type="button"
              variant="link"
              onClick={() => onSwitchMode("forgot-password")}
              className="px-0 h-auto text-xs font-medium"
            >
              Mot de passe oublié ?
            </Button>
          }
          type={showPassword ? "text" : "password"}
          leadingIcon={<LockIcon />}
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={passwordValidation.onBlur}
          disabled={isLoading}
          autoComplete="current-password"
          validation={passwordValidation}
          hideStatusIcon
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <ViewIcon className="w-4 h-4" />}
            </button>
          }
        />

        <Button
          type="submit"
          loading={isLoading}
          className={cn(
            "w-full h-12 text-base font-semibold rounded-xl mt-2",
            "bg-gradient-to-r from-primary to-accent hover:shadow-elevated hover:scale-[1.01]",
            "transition-[transform,box-shadow] duration-base ease-out-expo",
            "[&_*]:!text-primary-foreground",
          )}
        >
          {isLoading ? "Connexion…" : "Se connecter"}
        </Button>
      </form>
    </AuthCard>
  );
}
